export const site = {
  title: 'Move your legacy BI to Amazon Quick',
  description: 'We migrate dashboards, data models, and security from ten legacy BI platforms to Amazon Quick, then put AI agents on top of the same data.',
  // Bold follow-up line under the hero description. Leave empty to hide it.
  tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
  // Hero primary button: scrolls to the section with this id.
  heroPrimary: { label: 'Find your platform', target: 'platforms' },
  // Feature row under the hero buttons. `icon` keys map to glyphs in Home.jsx.
  heroBadges: [
    { icon: 'clock', label: 'Live in 45 Framework' },
    { icon: 'shield', label: 'AWS-funded proofs of concept' },
    { icon: 'bolt', label: 'Built on Amazon Quick' },
  ],
  // Empty means show all configured dashboards. Otherwise use exact folder names.
  dashboardFolders: [],
  // Home page "Explore use cases" grid. Off on the BI site: the
  // "Migrations we deliver" platform cards already link to every use case.
  showUseCaseGrid: false,
  contact: {
    enabled: true,
    title: 'What could your migration look like?',
    description: 'Talk with West Loop Strategy about moving your legacy BI to Amazon Quick, backed by AWS funding.',
    buttonLabel: 'Talk to our team',
    email: 'info@westloop.io',
    subject: 'BI migration to Amazon Quick',
    // Company LinkedIn page, shown as a button in the footer. Leave empty to hide it.
    linkedin: 'https://www.linkedin.com/company/westloopstrategy/',
  },
  event: {
    // Off until there's an event to promote. Fill in the
    // title, booth, and a logo in public/logos/, then set enabled: true.
    enabled: false,
    title: 'Meet us at [event name]!',
    description: 'Find us at booth #[number]',
    logo: '',
    logoAlt: '',
  },
  // Hero right-column CTA card (see QuickTryCard). A url starting with "/"
  // stays on this site; a full https:// url opens in a new tab.
  quickTry: {
    enabled: true,
    icon: 'clock',
    title: "Let's get started",
    description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
    buttonLabel: 'Start my migration',
    url: '/contact',
  },
  // Quick site's "Five stages" section -- not used on the BI site.
  journey: { enabled: false, stages: [] },
  // Numbered migration workflow with Automated / AI-led tags, the
  // "repeats per wave" note, and the typical-vs-West-Loop comparison.
  workflow: {
    enabled: true,
    eyebrow: 'Automation and AI at every step',
    title: 'A migration that asks less of your team',
    description: 'We use the metadata in your existing BI footprint to plan, convert, test, and train. Your people review exceptions and sign off on waves instead of testing every report by hand.',
    steps: [
      {
        tag: 'Automated',
        title: 'Automated assessment',
        description: 'We extract metadata from your current BI footprint instead of interviewing every team.',
        bullets: ['Reports, data sources, and calculations', 'Usage logs: who opens what, and how often', 'Users, groups, and security rules'],
      },
      {
        tag: 'AI-led',
        title: 'Wave planning',
        description: 'AI groups the estate into migration waves using that metadata.',
        bullets: ['Ranked by usage and business value', 'Sized by complexity and dependencies', 'Unused reports flagged for retirement'],
      },
      {
        tag: 'Automated',
        title: 'Automated conversion',
        description: 'Each wave is rebuilt on Amazon Quick with programmatic tooling.',
        bullets: ['Data models and calculations converted', 'Dashboards and paginated reports rebuilt', 'Security mapped to row-level access'],
      },
      {
        tag: 'AI-led',
        title: 'AI-led UAT and QA',
        description: 'AI checks every migrated report against the original before your team sees it.',
        bullets: ['Test cases generated from report metadata', 'Numbers compared side by side with the legacy tool', 'Only exceptions go to your team for sign-off'],
      },
      {
        tag: 'Automated',
        title: 'Enablement and training',
        description: 'Training is built for each wave from usage data, so people learn the reports they use.',
        bullets: ['Role-based training per wave', 'Report-by-report guides for top users', 'Legacy licenses retired as waves go live'],
      },
    ],
    repeatNote: {
      lead: 'Steps 3 to 5 repeat for each wave.',
      text: 'Every wave goes live and gets trained before the next one starts, so value lands early and risk stays small.',
    },
    compare: {
      typical: {
        title: 'A typical migration',
        bullets: ['Weeks of discovery interviews with report owners', 'Business users test every report by hand', 'One big-bang cutover with generic training'],
      },
      ours: {
        title: 'The West Loop approach',
        bullets: ['Discovery pulled from metadata and usage logs', 'AI runs the testing, and your team reviews only exceptions', 'Wave-by-wave go-lives with training tied to each wave'],
      },
    },
  },
  // "Migrations we deliver" heading. The cards themselves are the use
  // cases in useCases.js (one per platform), each linking to its page.
  platforms: {
    enabled: true,
    title: 'Migrations we deliver',
    description: 'Pick your current platform to see what moves and where it lands on AWS.',
  },
  // Four-step "How every migration runs" band.
  howItRuns: {
    enabled: true,
    title: 'How every migration runs',
    steps: [
      { title: 'Inventory and assess', description: 'Catalog every report, data source, and user, then rank by usage and business value.' },
      { title: 'Convert', description: 'Rebuild models, calculations, and security on AWS, using programmatic tooling where the platform allows.' },
      { title: 'Validate', description: 'Check numbers side by side against the legacy tool before anyone switches.' },
      { title: 'Cut over', description: 'Train users, move subscriptions, and retire the legacy licenses.' },
    ],
  },
  // AWS funding band.
  funding: {
    enabled: true,
    eyebrow: 'Supported by AWS funding',
    title: 'AWS programs can offset the cost of migrating',
    note: 'Eligibility and amounts are set by AWS and confirmed with your AWS account team.',
    programs: [
      { title: 'Assessment', description: 'Inventory and migration plan, often funded for eligible customers' },
      { title: 'Proof of concept', description: 'Your first dashboards migrated through Live in 45, with AWS co-investment' },
      { title: 'Full migration', description: 'Support for moving your full report estate and data onto AWS' },
    ],
  },
};
